import Joi from 'joi';
import api from '../../api';
import { contentSchema, paginationSchema } from '../schemas';

/**
 * Get a content by its ID.
 * @param {string} contentId
 * @returns {Promise<Content>}
 */
export const getContent = async (contentId) => {
  const result = await api.get(`/content-management/v1-0/admin/contents/${contentId}`);

  try {
    await contentSchema.validateAsync(result.data, { allowUnknown: true });
  } catch (error) {
    throw new Error(`API response do not match Content Schema : ${error.message}`);
  }

  return result.data;
};
/**
 * Get all contents
 * @param {object} queryParams
 * @param {number} queryParams.pageNumber
 * @param {number} queryParams.pageSize
 * @param {string|null} [queryParams.contentTypeId]
 * @param {string|null} [queryParams.search]
 * @param {string|null} [queryParams.sort]
 * @param {ContentStatus|null} [queryParams.status]
 * @returns {Promise<{data: Content[], pagination: Pagination}>}
 */
export const getAllContents = async (queryParams) => {
  const result = await api.get('/content-management/v1-0/admin/contents', { params: queryParams });

  try {
    const schema = Joi.object({
      data: Joi.array().items(contentSchema).required(),
      pagination: paginationSchema,
    });
    await schema.validateAsync(result.data, { allowUnknown: true });
  } catch (error) {
    throw new Error(`API response do not match All Contents Schema : ${error.message}`);
  }

  return result.data;
};
/**
 * Update a Content
 * @param {string} contentId
 * @param {object} updatedContent
 * @param {string} updatedContent.name
 * @param {number} version
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const updateContent = async (contentId, updatedContent, version) => api.patch(
  `/content-management/v1-0/admin/contents/${contentId}`,
  updatedContent,
  {
    headers: {
      'X-Presumed-Version': version,
    },
  },
);
/**
 * Create a new Content
 * @param {object} newContent
 * @param {string} newContent.contentTypeId
 * @param {string} newContent.name
 * @param {string} newContent.localeId
 * @param {object} newContent.data
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const postContent = async (newContent) => api
  .post('/content-management/v1-0/admin/contents', newContent);
/**
 * Archive a Content
 * @param {string} contentId
 * @param {number} version
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const postArchiveContent = async (contentId, version) => api.post(
  `/content-management/v1-0/admin/contents/${contentId}:archive`,
  undefined,
  {
    headers: {
      'X-Presumed-Version': version,
    },
  },
);
/**
 * Restore a Content
 * @param {string} contentId
 * @param {number} version
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const postRestoreContent = async (contentId, version) => api.post(
  `/content-management/v1-0/admin/contents/${contentId}:restore`,
  undefined,
  {
    headers: {
      'X-Presumed-Version': version,
    },
  },
);
/**
 * Duplicate a Content
 * @param {string} contentId
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const postDuplicateContent = async (contentId) => api
  .post(`/content-management/v1-0/admin/contents/${contentId}:duplicate`);
